import { all, call, cancelled, delay, join, put, race, select, spawn, take } from 'redux-saga/effects';
import { NEXT_PAGE, SUBMIT } from '../../../components/FlowStateElement/elementTypes';
import { MATRIX } from '../../../constants/questionTypes';
import sagaConfig from '../../../utils/configureSaga';
import localStorage from '../../../utils/localStorage';
import { handleStatePageClass, handleStatePageClassClear } from '../../../utils/statePageClass';
import DEFAULT_THANK_STATE from '../../FlowState/defaultThankState';
import { CHAT, THANK } from '../../FlowState/stateTypes';
import { getMeasurementDataForInterpolation } from '@pisano/react-translator';
import {
  clearResponsesOnState,
  sendChannelQuotaLog,
  sendPostMessage,
  setFeedbackResponse,
  setIsSubmitError,
  setSubmissionStatus,
  setIsChangingState,
  setPreventMultipleFeedback,
  setQuestion,
  setupCurrentState,
} from '../actions';
import { FLOW_ANSWERS, LOAD_FLOW_SUCCESS, OFFLINE_SAVE_FAILED, RESET_FLOW, SUBMISSION_STATUS } from '../constants';
import { selectIsSubmitBlocked } from '../selectors';
import { handleLinkChannelStore } from '../helpers/linkChannelStores.js';
import getFeedbackFromState from '../utils/getFeedbackFromState';
import { isMatrixResponseComplete } from '../utils/matrixValidation';
import { removeIncompleteSurveyByFeedbackId } from '../utils/incompleteSurveys.js';
import { handleWidgetStore } from '../../../../../feedback-web/containers/Widget/helpers/widgetStores.js';
import conditionRulesEngine from '../utils/conditionRulesEngine';
import { applyConditionRules } from './questionSaga';
import { handleCaptchaBeforeFeedbackCreation } from './captchaSaga';
import { submitFeedbackSaga } from './submitFeedbackSaga';

export const configureSaga = sagaConfig();
configureSaga.configureHooks({ postCreateFeedbackHook: null });

export function isQuestionsAnswered(state, hasNextStateButton) {
  const { currentState, questions } = state.flow;
  for (let i = 0; i < currentState.elements.length; i++) {
    const elementId = currentState.elements[i];
    const element = state.flow.elements[elementId];
    const question = questions[elementId];
    const { isVisible } = state.flow.elements[elementId];
    if (!question || !isVisible) continue;
    const isMatrix = element?.question?.style === MATRIX;

    if (!question.isValid) {
      return false;
    }

    if (isMatrix) {
      if (!isMatrixResponseComplete(element, question.response, { isQuestionRequired: question.isRequired })) {
        return false;
      }
      if (!hasNextStateButton && !question.response) {
        return false;
      }
      continue;
    }

    if (question.isRequired && (!question.response || !question.response.length)) {
      return false;
    }
    if (!hasNextStateButton && !question.response) {
      return false;
    }
    if (
      question.isMultiSelect &&
      question.minSelectionCount &&
      question.response.length &&
      question.response.length < question.minSelectionCount
    ) {
      return false;
    }
  }
  return true;
}

function questionMatchesTransitionRule(question, rule) {
  if (!question) return false;
  if (question.id !== rule.elementId) {
    return false;
  }

  const { response } = question;

  if (!response) return false;

  const { operator, value, optionIds, statementId } = rule;

  switch (operator) {
    case 'equal':
      return response === value;

    case 'contains':
      return response.includes(value);

    case 'not-contains':
      return !response.includes(value);

    case 'regex':
      return new RegExp(value, 'i').test(response);

    default:
      return optionIds.some((optionId) => matchesRule(response, optionId, statementId));
  }
}

function matchesRule(response, optionId, statementId) {
  if (!Array.isArray(response)) {
    return response === optionId;
  }

  return response.some((res) => {
    if (typeof res === 'string') {
      return res === optionId;
    }
    const hasStatementOptionId = 'statementOptionId' in res;

    if (hasStatementOptionId && statementId !== undefined) {
      return res.statementOptionId === optionId && res.statementId === statementId;
    }
    return res.statementOptionId === optionId;
  });
}

function findIfStateHasTransition(state) {
  const { questions, currentState } = state.flow;
  // INFO: you can get this code back
  // .filter((id) => elements[id].isVisible) // Only visible elements
  const allQuestions = Object.keys(questions).reduce((xs, id) => ({ ...xs, [id]: questions[id] }), {});
  const matchingTransitions = state.flow.transitions
    .filter((transition) => transition.fromStateId === currentState.id)
    .filter((transition) =>
      transition.rules
        .map((rule) => questionMatchesTransitionRule(allQuestions[rule.elementId], rule))
        .reduce((prev, cur) => prev && cur, true),
    );

  // TODO: What if there are multiple matching transitions?
  // Currently return the first one
  return matchingTransitions[0];
}

export function findNextStateInfo(state, source) {
  const transition = findIfStateHasTransition(state);
  let nextStateId =
    (transition && transition.toStateId) || state.flow.currentState.nextStateId || DEFAULT_THANK_STATE.state.id;
  let nextState = state.flow.states[nextStateId];

  if (source === SUBMIT) {
    // If the source is submit, immediately go to the thank state.
    // If there is no custom thank state, go to the default one.
    while (nextState.style !== THANK && nextState.style !== CHAT) {
      nextStateId = nextState.nextStateId || DEFAULT_THANK_STATE.state.id;
      nextState = state.flow.states[nextStateId];
    }
  }

  return nextState;
}

export function* nextStateSaga({ payload: { stateChangeParams } }) {
  const state = yield select();
  if (state.flow.isChangingState) return;

  // Both automatic transitions and Next Page must keep the accepted answers
  // unchanged until navigation or submission finishes.
  const shouldLock = isQuestionsAnswered(state, stateChangeParams.hasNextStateButton);
  if (shouldLock) yield put(setIsChangingState(true));

  let wasFlowReplaced = false;

  try {
    const { flowReplaced } = yield race({
      flowReplaced: take([LOAD_FLOW_SUCCESS, RESET_FLOW]),
      transition: call(changeState, state, stateChangeParams, shouldLock),
    });

    wasFlowReplaced = Boolean(flowReplaced);

    // Automatic response processing must also stop when its flow is replaced.
    return wasFlowReplaced;
  } finally {
    // Loading/resetting the flow clears its locks. An old transition no longer owns them.
    if (shouldLock && !wasFlowReplaced) yield put(setIsChangingState(false));
  }
}

function* changeState(state, stateChangeParams, questionsAnswered) {
  const { source } = stateChangeParams;
  if (questionsAnswered) {
    const nextState = findNextStateInfo(state, source);

    const nextStateIsThankOrChat = nextState.style === THANK || nextState.style === CHAT;
    if (state.flow.isSingleElementPreview && nextStateIsThankOrChat) {
      return;
    }

    // If next state is thank or chat, or next state request comes from a submit button,
    // create the feedback after setting up the current state.
    const shouldCreateFeedback = !state.flow.isPreview && (nextStateIsThankOrChat || source === SUBMIT);

    if (shouldCreateFeedback) {
      if (selectIsSubmitBlocked(state)) return;

      if (yield* handleCaptchaBeforeFeedbackCreation(state, stateChangeParams)) {
        return;
      }

      // If error message is shown, clear it. Before requesting to create feedback.
      yield put(setIsSubmitError(null));
    }

    yield delay(250);

    if (shouldCreateFeedback) {
      const submission = yield createFeedback(nextState);

      if (submission?.status === 'queued') {
        // Local persistence completes the survey, but no server response exists.
        yield put(setSubmissionStatus(SUBMISSION_STATUS.QUEUED));
        yield* completeQueuedSubmission(state, nextState, submission);
      } else if (submission?.status === 'unsaved') {
        // The answers stay on the page; only a later online attempt may resubmit them.
        yield put(setIsSubmitError({ code: OFFLINE_SAVE_FAILED }));
        handleStatePageClassClear();
      } else if (submission?.response) {
        const httpResponse = submission.response;
        let body = null;
        try {
          body = JSON.parse(httpResponse.responseText);
        } catch {
          body = {};
        }
        // Only do transition to next state if feedback creation is successful
        // TODO, currently no error message is shown to the user with a notification or toast.
        if (httpResponse.status === 200) {
          yield put(setSubmissionStatus(SUBMISSION_STATUS.COMPLETED));
          let targetState = nextState;
          if (body.measurement_redirect_state_key) {
            const redirectStateKey = body.measurement_redirect_state_key;
            const redirectState = Object.values(state.flow.states).find((s) => s.key === redirectStateKey);
            if (redirectState) {
              targetState = redirectState;
            }
          }

          targetState.previousState = state.flow.currentState;
          yield put(setupCurrentState(targetState));

          const hooks = configureSaga.getHooks();
          // If it's a chat state in web, go to the chat.
          if (hooks.postCreateFeedbackHook) {
            yield* hooks.postCreateFeedbackHook(body, yield select());
          }

          handleStatePageClass(targetState.style);
        } else if (httpResponse.status === 400 && body.code === 13045) {
          yield put(setSubmissionStatus(SUBMISSION_STATUS.COMPLETED));
          yield put(setPreventMultipleFeedback(true));
        } else {
          // Submit error
          yield put(setIsSubmitError(body));
          handleStatePageClassClear();
        }
      }
    } else {
      nextState.previousState = state.flow.currentState;
      yield put(setupCurrentState(nextState));
      handleStatePageClass(nextState.style);
    }
  } else {
    if (source !== NEXT_PAGE && source !== SUBMIT) {
      return;
    }
    const { currentState, questions } = state.flow;
    for (let i = 0; i < currentState.elements.length; i += 1) {
      const elementId = currentState.elements[i];
      const question = questions[elementId];
      const element = state.flow.elements[elementId];
      if (!question || !element?.isVisible) continue;

      const isMatrix = element?.question?.style === MATRIX;
      if (
        isMatrix &&
        !isMatrixResponseComplete(element, question.response, { isQuestionRequired: question.isRequired })
      ) {
        yield put(setQuestion({ questionId: elementId, hasRequiredError: true }));
        continue;
      }

      if (
        !isMatrix &&
        question.isRequired &&
        (question.response === null || (Array.isArray(question.response) && question.response.length === 0))
      ) {
        yield put(setQuestion({ questionId: elementId, hasRequiredError: true }));
      }
    }
  }
}

export function* previousStateSaga() {
  const state = yield select();
  if (state.flow.isChangingState) return;
  const { previousState } = state.flow.currentState;
  // Clear responses and navigate back to the previous state
  yield all([put(clearResponsesOnState({ state: state.flow.currentState })), put(setupCurrentState(previousState))]);
  // Re-evaluate condition rules after clearing responses to ensure proper visibility/required state
  // This handles cases where rules target elements in earlier states based on answers in the current state
  const updatedState = yield select();
  yield* applyConditionRules(updatedState);
}

export function removeFlowAnswersFromLocalStorage(nodeId) {
  // when user submit the flow, remove flow answers from localStorage
  localStorage.removeItem(FLOW_ANSWERS.concat(nodeId));
}

function markSurveyCompletedLocally(state) {
  removeFlowAnswersFromLocalStorage(state.node.id);

  if (state.node.preventMultipleFeedback) {
    handleLinkChannelStore(state.node.id, { firstNow: new Date(), isLinkChannelDisplayedBefore: true });
  }

  if (state.widget?.preventMultipleFeedback) {
    handleWidgetStore(state.node.id, { lastFeedbackSubmitDate: new Date(), isWidgetDisplayedBefore: true });
  }
}

/**
 * Shows the survey's local completion for a durably queued final. Server results
 * such as scores, redirect states, chat and success events do not exist yet.
 */
function* completeQueuedSubmission(state, nextState, { submissionId }) {
  const targetState =
    nextState.style === THANK
      ? nextState
      : state.flow.states[DEFAULT_THANK_STATE.state.id] || DEFAULT_THANK_STATE.state;

  try {
    markSurveyCompletedLocally(state);
  } catch (cleanupErr) {
    console.warn('Non-critical post-feedback cleanup error:', cleanupErr);
  }

  targetState.previousState = state.flow.currentState;
  yield put(setupCurrentState(targetState));
  handleStatePageClass(targetState.style);

  // The record is already durable; the QUEUED lock keeps this to one event per submission.
  yield put(
    sendPostMessage({
      type: 'PSN_FEEDBACK_QUEUED',
      data: {
        flow_id: state.flow.id,
        flow_name: state.flow.name,
        submission_id: submissionId,
        status: 'queued',
      },
    }),
  );
}

function* submitFinalFeedback(feedback) {
  try {
    return yield call(submitFeedbackSaga, { feedback });
  } catch (error) {
    // Detached tasks must return failures to the owning transition.
    return { status: 'failed', error };
  }
}

function* createFeedback(nextState) {
  const nextStateIsThank = nextState.style === THANK;
  const state = yield select();
  const feedback = getFeedbackFromState(state);

  let submissionTask;
  try {
    yield put(setSubmissionStatus(SUBMISSION_STATUS.PENDING));
    // A sent POST cannot be rolled back by cancelling its page transition.
    submissionTask = yield spawn(submitFinalFeedback, feedback);
    const result = yield join(submissionTask);

    if (result.error?.kind === 'storage') return { status: 'unsaved' };

    if (result.status === 'failed') {
      // Without an HTTP response, the failure is shown as a generic submit error.
      return {
        status: 'failed',
        response: result.error?.response || { status: 400, responseText: JSON.stringify({ code: 13000 }) },
      };
    }

    if (result.status !== 'submitted') return result;

    let { body } = result;

    if (body.measurement_scores) {
      const measurementData = getMeasurementDataForInterpolation(body);
      body = { ...body, ...measurementData };
    }

    yield put(setFeedbackResponse(body));

    // When is_internal_feedback: true, that means the feedback is coming from Pisano dashboard.
    // So if it's internal feedback, don't add delay before throw event.
    // Same usage is also present in vision and can be searched as is_internal_feedback.
    const isInternalFeedback = state.flow.launchContext.params.is_internal_feedback === 'true';

    // Check for redirect-to-store event from condition rules system
    const triggeredEvents = conditionRulesEngine.getTriggeredEvents(state);
    const hasRedirectToStoreEvent = triggeredEvents.includes('redirect-to-store');

    if (hasRedirectToStoreEvent) {
      yield put(
        sendPostMessage({
          type: 'PSN_REDIRECT_TO_STORE',
          data: {
            flow_id: state.flow.id,
            flow_name: state.flow.name,
            feedback_id: body.id,
            has_chat: state.flow.currentState.style === CHAT,
          },
        }),
      );
    }

    yield put(
      sendPostMessage({
        type: 'PSN_END_OF_FLOW',
        data: {
          flow_id: state.flow.id,
          flow_name: state.flow.name,
          feedback_id: body.id,
          has_chat: state.flow.currentState.style === CHAT,
        },
      }),
    );

    yield put(
      sendPostMessage({
        type: 'PSN_FEEDBACK_SUBMITTED',
        delay: !isInternalFeedback && nextStateIsThank ? 3000 : 0,
        data: {
          flow_id: state.flow.id,
          flow_name: state.flow.name,
          feedback_id: body.id,
          has_chat: state.flow.currentState.style === CHAT,
        },
      }),
    );

    // Non-critical localStorage cleanup.
    // Errors here must never prevent the thank-page transition.
    try {
      markSurveyCompletedLocally(state);
      removeIncompleteSurveyByFeedbackId(body.id);
    } catch (cleanupErr) {
      console.warn('Non-critical post-feedback cleanup error:', cleanupErr);
    }

    if (state.node.channelQuota) {
      const { isActive, type } = state.node.channelQuota;
      if (isActive && type === 'submit') {
        yield put(sendChannelQuotaLog({ type: 'submitted' }));
      }
    }

    return result;
  } finally {
    const wasCancelled = yield cancelled();
    // Keep replacement flows from submitting until the existing request settles.
    if (wasCancelled && submissionTask) yield join(submissionTask);
    yield put(setSubmissionStatus(SUBMISSION_STATUS.IDLE));
  }
}
